import { useState } from 'react'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { CouponInfo } from '@sportninja/common/hooks/useSessionManagement'

/**
 * Division information interface
 */
interface Division {
  schedule_id: string
}

/**
 * Props interface for the usePromoCode hook
 */
interface UsePromoCodeProps {
  division: Division
  isTeamRep: boolean
  promoCode: string
  setCouponInfo: (coupon: CouponInfo | null) => void
}

/**
 * 🔄 usePromoCode: Custom hook for promo code validation
 * Handles API calls for coupon validation and manages error states
 */
export const usePromoCode = ({
  division,
  isTeamRep,
  promoCode,
  setCouponInfo,
}: UsePromoCodeProps) => {
  console.log('🔄 usePromoCode: Initializing promo code hook')

  const [promoCodeError, setPromoCodeError] = useState<string | null>(null)
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(false)

  /**
   * 🔄 validateCoupon: Validates the promo code with the API
   */
  const validateCoupon = async () => {
    console.log('🔄 validateCoupon: Validating promo code with API')

    try {
      setPromoCodeError(null)
      setIsPromoCodeValid(false)

      const res = await req(
        `/registration/${division?.schedule_id}/coupon/verify`,
        {
          method: 'POST',
          body: JSON.stringify({
            code: promoCode,
            registration_type_id: isTeamRep ? 1 : 2,
          }),
        }
      )

      setCouponInfo({
        discount: res?.data?.discount,
        type: res?.data?.discount_type,
        id: res?.data?.id,
        code: res?.data?.code,
        subtotal: res?.data?.subtotal,
        discount_subtotal: res?.data?.discount_subtotal,
        discount_amount: res?.data?.discount_amount,
        deposit_subtotal: res?.data?.deposit_subtotal,
        deposit_discount_subtotal: res?.data?.deposit_discount_subtotal,
        deposit_discount_amount: res?.data?.deposit_discount_amount,
        max_uses: res?.data?.max_uses,
        is_active: res?.data?.is_active,
        is_team: res?.data?.is_team,
        is_player: res?.data?.is_player,
        is_deposit: res?.data?.is_deposit,
        is_installment: res?.data?.is_installment,
        applies_to_deposit: res?.data?.applies_to_deposit,
        organization_id: res?.data?.organization_id,
        registration_id: res?.data?.registration_id,
        start_date: res?.data?.start_date,
        end_date: res?.data?.end_date,
      })

      setIsPromoCodeValid(true)
      console.log('✅ validateCoupon: Promo code validated successfully')
    } catch (e) {
      console.error('❌ validateCoupon: Error validating promo code', e)
      const msg = getErrorMessage(e)
      setPromoCodeError(msg.replace(/^Invalid fields:\n?/i, ''))
    }
  }

  const resetPromoCode = () => {
    setIsPromoCodeValid(false)
    setPromoCodeError(null)
  }

  return {
    validateCoupon,
    promoCodeError,
    isPromoCodeValid,
    resetPromoCode,
  }
}
